import { GeoJsonLayer } from '@deck.gl/layers';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import { alpha, styled } from '@mui/material/styles';
import { useQuery } from '@tanstack/react-query';
import locationPinAmber from 'assets/images/location-pin-amber.svg?no-inline';
import locationPinBlue from 'assets/images/location-pin-blue.svg?no-inline';
import locationPinGreen from 'assets/images/location-pin-green.svg?no-inline';
import { isNumber } from 'lodash';
import { FC, Fragment, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMap } from 'react-map-gl/mapbox';
import { getSingleLocationOptions } from 'src/clients/locations/Device Locations API/@tanstack/react-query.gen';
import { CslCmpDeviceLocationsApiContractsGeoJsonFeatureV1 } from 'src/clients/locations/Device Locations API/types.gen';
import { LocationsMapDisclaimer } from 'src/locations/LocationsMapDisclaimer';
import { NoLocation } from 'src/locations/NoLocation';
import { AppMap } from 'src/map/AppMap';

type LocationDetailsMapProps = {
  imsi: string;
  parentInitialPending?: boolean;
  parentError?: boolean;
};

/** These types to be replaced by generated types from the API when available */
type LocationActivity = '24h' | '30d' | '90d';
type LocationResponsePoint = CslCmpDeviceLocationsApiContractsGeoJsonFeatureV1;
type LocationMapFeature = GeoJSON.Feature<GeoJSON.Point, { activity?: LocationActivity }>;

const isLocationMapFeature = (
  feature: LocationResponsePoint | null,
): feature is LocationMapFeature => {
  if (feature?.type !== 'Feature') {
    return false;
  }

  if (feature.geometry?.type !== 'Point' || !Array.isArray(feature.geometry.coordinates)) {
    return false;
  }

  const [lon, lat] = feature.geometry.coordinates;

  if (!isNumber(lon) || !isNumber(lat)) {
    return false;
  }

  const activity =
    feature.properties && typeof feature.properties === 'object'
      ? (feature.properties as { activity?: unknown }).activity
      : undefined;

  if (activity !== undefined && activity !== '24h' && activity !== '30d' && activity !== '90d') {
    return false;
  }

  return true;
};

const isNoLocationFeature = (feature: LocationResponsePoint | null): boolean => {
  if (!feature) {
    return true;
  }

  if (feature.type !== 'Feature') {
    return false;
  }

  return feature.geometry === null;
};

const MapContainer = styled(Box)(() => ({
  width: '100%',
  height: '100%',
  position: 'relative',
  borderRadius: '8px',
  overflow: 'hidden',
  flexGrow: 1,
  flexShrink: 1,
}));
MapContainer.displayName = 'MapContainer';

const MapOverlay = styled(Box)(() => ({
  position: 'absolute',
  top: '0',
  left: '0',
  width: '100%',
  height: '100%',
  zIndex: 2,
}));
MapOverlay.displayName = 'MapOverlay';

const NoLocationOverlay: FC = () => {
  return (
    <MapOverlay
      data-testid="sim-location-detail-overlay-no-location"
      sx={(theme) => ({
        backgroundColor: theme.palette.background.default,
        pointerEvents: 'all',
        borderColor: theme.palette.border,
        borderWidth: '1px',
        borderStyle: 'solid',
        textAlign: 'center',
      })}
    >
      <NoLocation />
    </MapOverlay>
  );
};

export const LocationDetailsMap: FC<LocationDetailsMapProps> = (props) => {
  const { imsi, parentInitialPending = false, parentError = false } = props;

  const { t } = useTranslation();

  const singleLocationOptions = getSingleLocationOptions({
    query: {
      DeviceId: imsi,
    },
  });

  const {
    data,
    status: locationStatus,
    fetchStatus: locationFetchStatus,
  } = useQuery({
    ...singleLocationOptions,
    enabled: !!imsi,
    meta: {
      isLocationsDetails: true,
    },
  });

  const status = locationStatus;
  const childInitialPending = status === 'pending' && locationFetchStatus === 'fetching';
  const effectiveInitialPending = parentInitialPending || childInitialPending;

  const firstFeature = locationStatus === 'success' ? data.features?.[0] ?? null : null;
  const noLocation = status === 'success' && isNoLocationFeature(firstFeature);
  const validFeature = isLocationMapFeature(firstFeature) ? firstFeature : null;
  const isInvalidFeature = status === 'success' && !noLocation && !validFeature;
  const effectiveError = parentError || status === 'error' || isInvalidFeature;
  const coordinates = validFeature ? validFeature.geometry.coordinates : null;

  const map = useMap();
  const mapRef = map['sim-location-detail'];

  useEffect(() => {
    if (mapRef && status === 'success') {
      const [lon, lat] = coordinates ?? [0, 0];
      const center: [number, number] = [lon, lat];

      const zoom = coordinates ? 10 : 2;

      mapRef.flyTo({
        zoom,
        center,
      });
    }
  }, [mapRef, status, coordinates]);

  const layers = useMemo(() => {
    if (status === 'success' && validFeature && coordinates) {
      return [
        new GeoJsonLayer<LocationMapFeature['properties']>({
          id: `device-${imsi}`,
          data: validFeature,
          parameters: { blendColorSrcFactor: 'dst-alpha' },
          pointType: 'icon',
          getIconSize: 43,
          getIcon: (d: LocationMapFeature) => {
            const commonIconProperties = { height: 630, width: 512, anchorY: 630 };
            const activity = d.properties.activity;

            if (activity === '24h') {
              return { ...commonIconProperties, url: locationPinGreen };
            }

            if (activity === '30d') {
              return { ...commonIconProperties, url: locationPinAmber };
            }

            return { ...commonIconProperties, url: locationPinBlue };
          },
        }),
      ];
    }

    return [];
  }, [status, imsi, validFeature, coordinates]);

  return (
    <Fragment>
      <MapContainer>
        <AppMap testId="locationDetails.map.map" id="sim-location-detail" layers={layers} />
        {status === 'success' && noLocation ? <NoLocationOverlay /> : null}
        {effectiveInitialPending ? (
          <MapOverlay
            data-testid="locationDetails.map.overlay-pending"
            sx={(theme) => ({
              pointerEvents: 'none',
              backgroundColor: alpha(theme.palette.border, 0.6),
            })}
          />
        ) : null}
        {effectiveError ? (
          <MapOverlay
            data-testid="locationDetails.map.overlay-error"
            sx={(theme) => ({
              pointerEvents: 'none',
              backgroundColor: alpha(theme.palette.border, 0.6),
            })}
          >
            <Stack height="100%" alignItems="center" justifyContent="center">
              {t('common.somethingWrong')}
            </Stack>
          </MapOverlay>
        ) : null}
      </MapContainer>
      {status === 'success' && !effectiveInitialPending && !effectiveError ? (
        <Box flexGrow="0" flexShrink="0" mt={12}>
          <LocationsMapDisclaimer testId="locationDetails.map.disclaimer" />
        </Box>
      ) : null}
    </Fragment>
  );
};
